import React from "react";
import { ErrorBoundary, type FallbackProps } from "react-error-boundary";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { reportToRollbar } from "@/lib/rollbarReport";

/**
 * FeatureErrorBoundary — a scoped, in-place fallback used to **contain**
 * failures inside a single feature widget (NotificationBell, RealtimeSync,
 * CircleView, BreakfastEventList, etc).
 *
 * Contrast with `GlobalErrorBoundary`, which is a full-screen "service
 * interruption" screen we only want to hit as an absolute last resort.
 * This boundary draws a subtle bordered card inline with the rest of the
 * UI so the host page (header, nav, other widgets) keeps working even if
 * one feature module throws.
 *
 * The `feature` prop is a stable identifier forwarded to Rollbar so
 * production error reports can be correlated to a specific feature boundary.
   */

export interface FeatureErrorBoundaryProps {
  /**
   * Stable, kebab-case identifier for this boundary. Used for telemetry
   * tagging (`feature`) and for the error
   * message logged to the console. Required — boundaries without a name
   * are very hard to debug in aggregate.
   */
  feature:   string;
  children:  React.ReactNode;
  /**
   * Optional override for the fallback UI. Default is a constrained
   * bordered card with a Retry button. Provide a custom renderer for
   * tiny inline widgets (e.g. a notification bell icon) where even the
   * default card is too large.
   */
  fallback?: (props: FallbackProps & { feature: string }) => React.ReactNode;
  /**
   * Optional reset callback fired alongside the default `resetErrorBoundary`.
   * Useful for clearing any feature-local state that led to the crash
   * (e.g. `queryClient.resetQueries` on a specific key).
   */
  onReset?:  () => void;
}

function DefaultFeatureFallback({
  error,
  resetErrorBoundary,
  feature,
}: FallbackProps & { feature: string }) {
  const message = error instanceof Error ? error.message : String(error);
  const isDev   = import.meta.env.MODE === "development";

  return (
    <div
      role="alert"
      data-feature={feature}
      className="w-full rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm animate-fade-in"
    >
      <div className="flex items-start gap-3">
        <div className="h-8 w-8 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="h-4 w-4 text-destructive" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-foreground">
            This section failed to load
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            We've logged the issue. You can keep using the rest of the app
            or try again.
          </p>
          {isDev && (
            <pre className="mt-2 max-h-24 overflow-auto rounded-md bg-muted/50 p-2 text-[10px] text-destructive font-mono whitespace-pre-wrap select-all">
              [{feature}] {message}
            </pre>
          )}
          <button
            type="button"
            onClick={resetErrorBoundary}
            className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-border/60 bg-card px-3 py-1.5 text-xs font-semibold hover:bg-accent/60 transition-colors tap-highlight no-select"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Retry
          </button>
        </div>
      </div>
    </div>
  );
}

export function FeatureErrorBoundary({
  feature,
  children,
  fallback,
  onReset,
}: FeatureErrorBoundaryProps) {
  const renderFallback = React.useCallback(
    (props: FallbackProps) =>
      fallback
        ? fallback({ ...props, feature })
        : <DefaultFeatureFallback {...props} feature={feature} />,
    [feature, fallback],
  );

  return (
    <ErrorBoundary
      fallbackRender={renderFallback}
      onError={(error, info) => {
        console.error(
          `[FeatureErrorBoundary:${feature}]`,
          error,
          info.componentStack,
        );
        reportToRollbar(error, {
          tags:  { feature, boundary: "feature" },
          extra: { componentStack: info.componentStack },
        });
      }}
      onReset={onReset}
    >
      {children}
    </ErrorBoundary>
  );
}
